import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { FunnelSimple, X, Lock, LockOpen, Eraser, Star, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { saveUserDefault, clearUserDefault, loadUserDefault } from "@/lib/filterStore";

export default function FilterPanel({ value, onChange, onReset, onSave, locks, onLocksChange }) {
  const [themes, setThemes] = useState([]);
  const [hasUserDefault, setHasUserDefault] = useState(() => !!loadUserDefault());

  useEffect(() => {
    api.themes().then(setThemes).catch(() => setThemes([]));
  }, []);

  const set = (k, v) => onChange({ ...value, [k]: v, page: 1 });
  const toggleLock = (k) => onLocksChange({ ...(locks || {}), [k]: !(locks || {})[k] });
  const isLocked = (k) => !!(locks || {})[k];
  const clearThemes = () => set("themes", null);

  const onSaveDefault = () => {
    if (saveUserDefault(value)) {
      setHasUserDefault(true);
      toast.success("Saved as your default view — every fresh load starts here");
    } else {
      toast.error("Couldn't save default");
    }
  };

  const onClearDefault = () => {
    if (clearUserDefault()) {
      setHasUserDefault(false);
      toast.info("Default cleared — fresh loads return to the built-in defaults");
    }
  };

  return (
    <div className="w-64 border-r border-[#E4E4E0] bg-[#FFFFFF] p-4 space-y-5 overflow-y-auto" data-testid="filter-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FunnelSimple size={14} weight="bold" className="text-[#E3000B]" />
          <span className="font-head font-bold text-xs uppercase tracking-wider">Filters</span>
        </div>
        <button onClick={onReset} className="text-gray-500 hover:text-[#E3000B]" data-testid="filter-reset" title="Reset (locked sections kept)">
          <X size={14} />
        </button>
      </div>

      <FieldGroup label="Minimum ROI %">
        <Input type="number" inputMode="numeric" value={value.min_roi ?? ""} onChange={e => set("min_roi", e.target.value === "" ? null : Number(e.target.value))}
          className="h-8 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-xs" data-testid="filter-min-roi" placeholder="20" />
      </FieldGroup>

      <FieldGroup label="Minimum Profit $">
        <Input type="number" value={value.min_profit ?? ""} onChange={e => set("min_profit", e.target.value === "" ? null : Number(e.target.value))}
          className="h-8 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-xs" data-testid="filter-min-profit" placeholder="5" />
      </FieldGroup>

      <FieldGroup label="Min Score">
        <Input type="number" value={value.min_score ?? ""} onChange={e => set("min_score", e.target.value === "" ? null : Number(e.target.value))}
          className="h-8 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-xs" data-testid="filter-min-score" placeholder="0-100" />
      </FieldGroup>

      <FieldGroup label="Sales Rank Range">
        <div className="flex gap-2">
          <Input type="number" value={value.min_sales_rank ?? ""} onChange={e => set("min_sales_rank", e.target.value === "" ? null : Number(e.target.value))}
            className="h-8 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-xs" data-testid="filter-min-rank" placeholder="min" />
          <Input type="number" value={value.max_sales_rank ?? ""} onChange={e => set("max_sales_rank", e.target.value === "" ? null : Number(e.target.value))}
            className="h-8 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-xs" data-testid="filter-max-rank" placeholder="max" />
        </div>
      </FieldGroup>

      <FieldGroup label="Min Seller Count (BL)">
        <Input type="number" value={value.min_seller_count ?? ""} onChange={e => set("min_seller_count", e.target.value === "" ? null : Number(e.target.value))}
          className="h-8 bg-[#F6F6F3] border-[#E4E4E0] font-mono text-xs" data-testid="filter-min-sellers" placeholder="1" />
      </FieldGroup>

      <FieldGroup
        label="Themes"
        actions={
          <div className="flex items-center gap-1">
            <button
              onClick={clearThemes}
              disabled={!value.themes?.length}
              className={`p-0.5 rounded-sm ${value.themes?.length ? "text-gray-600 hover:text-[#FF3333]" : "text-gray-300 cursor-not-allowed"}`}
              title={value.themes?.length ? `Clear ${value.themes.length} selected` : "No themes selected"}
              data-testid="filter-themes-clear"
            >
              <Eraser size={12} weight="bold" />
            </button>
            <button
              onClick={() => toggleLock("themes")}
              className={`p-0.5 rounded-sm ${isLocked("themes") ? "text-[#E3000B]" : "text-gray-500 hover:text-[#E3000B]"}`}
              title={isLocked("themes") ? "Themes are locked — survives navigation back" : "Lock themes — keep selections when navigating back from a Set Detail"}
              data-testid="filter-themes-lock"
            >
              {isLocked("themes") ? <Lock size={12} weight="fill" /> : <LockOpen size={12} weight="bold" />}
            </button>
          </div>
        }
      >
        {value.themes?.length > 0 && (
          <div className="text-[10px] font-mono text-[#E3000B] mb-1" data-testid="filter-themes-count">
            {value.themes.length} selected
          </div>
        )}
        <div className="max-h-48 overflow-y-auto border border-[#E4E4E0] rounded-sm p-2 space-y-1">
          {themes.map(t => {
            const checked = value.themes?.includes(t) || false;
            return (
              <label key={t} className="flex items-center gap-2 text-xs font-mono text-gray-800 cursor-pointer hover:text-black">
                <Checkbox
                  checked={checked}
                  onCheckedChange={(v) => {
                    const cur = value.themes || [];
                    const next = v ? [...cur, t] : cur.filter(x => x !== t);
                    set("themes", next.length ? next : null);
                  }}
                  className="h-3 w-3 rounded-sm border-[#E4E4E0] data-[state=checked]:bg-[#E3000B] data-[state=checked]:border-[#E3000B]"
                  data-testid={`filter-theme-${t.replace(/\s+/g, '-').toLowerCase()}`}
                />
                {t}
              </label>
            );
          })}
        </div>
      </FieldGroup>

      <FieldGroup label="ASIN Status">
        <div className="grid grid-cols-2 gap-1" data-testid="filter-asin-status">
          {[
            { v: null, label: "ALL", cls: "text-gray-800" },
            { v: "asin", label: "HAS ASIN", cls: "text-[#00A850]" },
            { v: "no_asin", label: "NO ASIN", cls: "text-[#FF3333]" },
            { v: "not_on_amazon", label: "NOT ON AMZ", cls: "text-gray-600" },
          ].map(o => {
            const active = (value.asin_status || null) === o.v;
            return (
              <button key={o.label} onClick={() => set("asin_status", o.v)}
                data-testid={`filter-asin-status-${o.v || "all"}`}
                className={`h-7 rounded-sm border font-mono text-[10px] font-bold tracking-wider transition-colors ${
                  active ? `border-[#E3000B] bg-[#E3000B]/10 ${o.cls}` : "border-[#E4E4E0] text-gray-500 hover:text-black hover:border-zinc-500"
                }`}>{o.label}</button>
            );
          })}
        </div>
      </FieldGroup>

      <FieldGroup
        label="Toggles"
        actions={
          <button
            onClick={() => toggleLock("toggles")}
            className={`p-0.5 rounded-sm ${isLocked("toggles") ? "text-[#E3000B]" : "text-gray-500 hover:text-[#E3000B]"}`}
            title={isLocked("toggles") ? "Toggles are locked — survives navigation back" : "Lock toggles — keep selections when navigating back from a Set Detail"}
            data-testid="filter-toggles-lock"
          >
            {isLocked("toggles") ? <Lock size={12} weight="fill" /> : <LockOpen size={12} weight="bold" />}
          </button>
        }
      >
        <ToggleRow label="Live Data Only" checked={value.live_only} onChange={v => set("live_only", v)} testId="filter-live-only" />
        <ToggleRow label="Previously Purchased" checked={value.previously_purchased} onChange={v => set("previously_purchased", v)} testId="filter-previously-purchased" />
        <ToggleRow label="Hide Not-On-Amazon" checked={value.exclude_unlisted} onChange={v => set("exclude_unlisted", v)} testId="filter-exclude-unlisted" />
        <ToggleRow label="Retired Only" checked={value.retired_only} onChange={v => set("retired_only", v)} testId="filter-retired-only" />
        <ToggleRow label="Active Only" checked={value.active_only} onChange={v => set("active_only", v)} testId="filter-active-only" />
        <ToggleRow label="Amazon In Stock" checked={value.in_stock_only} onChange={v => set("in_stock_only", v)} testId="filter-instock-only" />
        <ToggleRow label="Exclude Hazmat" checked={value.exclude_hazmat} onChange={v => set("exclude_hazmat", v)} testId="filter-no-hazmat" />
      </FieldGroup>

      <div className="space-y-2 pt-2 border-t border-[#E4E4E0]">
        <Button onClick={onSave} className="w-full h-8 bg-[#E3000B] text-white font-bold uppercase tracking-wider hover:bg-[#E6B800] rounded-sm" data-testid="filter-save">
          Save Filter
        </Button>
        <Button onClick={onSaveDefault} variant="outline"
          className="w-full h-8 rounded-sm border-[#E3000B]/40 bg-transparent text-[#E3000B] hover:bg-[#E3000B]/10 font-bold uppercase tracking-wider text-xs"
          data-testid="filter-save-default">
          <Star size={11} weight="fill" className="mr-1" />
          {hasUserDefault ? "Update Default View" : "Save as Default View"}
        </Button>
        {hasUserDefault && (
          <button onClick={onClearDefault}
            className="w-full text-[10px] font-mono text-gray-500 hover:text-[#FF3333] flex items-center justify-center gap-1"
            data-testid="filter-clear-default">
            <ArrowCounterClockwise size={10} /> Clear default · use built-ins
          </button>
        )}
      </div>
    </div>
  );
}

function FieldGroup({ label, actions, children }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">{label}</Label>
        {actions}
      </div>
      {children}
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-xs font-mono text-gray-800">{label}</span>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId} className="data-[state=checked]:bg-[#E3000B] h-4 w-7" />
    </div>
  );
}
